import { useCallback, useEffect, useState } from 'react';
import { initializeApp, getApps } from "firebase/app";
import { getAnalytics, isSupported } from "firebase/analytics";
import { AnalyticsCallOptions, CustomEventName, logEvent, setUserId } from '@firebase/analytics';
import { AnalyticEvent, AnalyticsEvents } from '@sweatpals/shared';

/**
 * @summary Initializes firebase scripts if not connected yet. Returns simplified and typed interface for capturing custom events
 *
 * @example At the root component of the app
 * useAnalytics();
 *
 * @example Anywhere in the app
 * const { logEventTyped } = useAnalytics();
 * // ...
 * logEventTyped('event_created', { eventId: '<<value>>', alias: '<<value>>' });
 */
export const useAnalytics = () => {
	const [isAnalyticsSupported, setIsAnalyticsSupported] = useState<boolean>(false);

	useEffect(() => {
		const firebaseConfig = {
			apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
			authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
			projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
			storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
			messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
			appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
			measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
		};

		if (typeof window !== undefined) {
			if (!getApps().length) {
				const app = initializeApp(firebaseConfig);
				getAnalytics(app);
			}

			isSupported()
				.then(supported => {
					setIsAnalyticsSupported(supported);
				});
		}
	}, []);

	const logEventTyped = useCallback(<T extends CustomEventName<AnalyticsEvents>>(event: T, metadata: AnalyticEvent<T>, options?: AnalyticsCallOptions) => {


		metadata = {
			...metadata,
			platform: 'web',
		}

		try {
			const analytics = getAnalytics();
			logEvent<AnalyticsEvents>(analytics, event, metadata, options);
			console.info('Analytics event logged: ', { event, metadata });
		} catch (e) {
			console.error('Error while logging analytical event', e);
		}
	}, []);

	const setGAUserId = useCallback((userId: string) => {
		try {
			const analytics = getAnalytics();
			setUserId(analytics, userId);
			console.info('Analytics user id set: ', userId);
		} catch (e) {
			console.error('Error while setting user id', e);
		}
	}, []);

	return {
		isAnalyticsSupported,
		logEventTyped,
		setGAUserId,
	};
};
